import { filterClasses } from "@swivl-mono/helpers";
import { Channel, EscalationStatus } from "@swivl-mono/types";
import { Button, ButtonVariant } from "@swivl-mono/ui";
import { HTMLProps, useMemo } from "react";
import { ChannelColors } from "../../../../Helpers/Conversations/Conversations.Helper";
import useHasPendingUpdates from "../../../../Hooks/useHasPendingUpdates";
import { SessionMessageData } from "../../../../Hooks/useSessionMessageData";
import MembersModel from "../../../../Models/Member/Members.Model";
import { PendingUpdateType } from "../../../../Models/Pending/Pending.Model";
import { UserConversationsInterface, UsersModel } from "../../../../Models/Users/Users.Model";
import { UsersOnlineManager } from "../../../../Models/Users/UsersOnline.Manager";

interface ConversationCardProps extends HTMLProps<HTMLDivElement> {
  conversation: UserConversationsInterface | undefined;
  sessionMessageData?: { [userId: number]: { [sessionId: number]: SessionMessageData } };
  selectedUserId?: number | undefined;
  className?: string;
  isEscalated?: boolean;
  isOnline?: boolean;
}

export const parseTime = (date: Date | undefined) => {
  if (!date) return "";
  const locale = navigator.language || 'en-US';
  return date.toLocaleString(locale, { hour: '2-digit', minute: '2-digit', hour12: true });
};

export function OpenConversationCard({ selectedUserId, conversation, sessionMessageData, className, isEscalated = false, isOnline = false, ...rest }: ConversationCardProps) {
  const isPending = useHasPendingUpdates([{ type: PendingUpdateType.SESSION_ESCALATION, id: conversation?.user?.id || "" }]);
  const membersMap = MembersModel.useState(s => s.membersMap);


  const { agentName, currentEscalation } = useMemo(() => {
    const escalation = conversation?.escalations ? Object.values(conversation.escalations).find(e => e.status === EscalationStatus.ACTIVE || e.status === EscalationStatus.PENDING || e.status === EscalationStatus.ASSIGNED) : undefined;
    if (!escalation) {
      return { agentName: "", currentEscalation: undefined };
    }
    const _agentName = escalation?.assignedMemberId ? membersMap?.[escalation?.assignedMemberId]?.agentName || "Unknown Agent" : "Unknown Agent";
    return { agentName: _agentName, currentEscalation: escalation };
  }, [conversation?.escalations, membersMap]);



  const handleArchiveUser = (userId: number | undefined) => {
    if (userId) {
      UsersModel.setArchivedForUser(userId, true);
    }
  };
  const firstLetterOfName = conversation?.name?.charAt(0) || conversation?.name?.charAt(0) || "";
  const isSelected = conversation?.user?.id === selectedUserId;

  return (
    <div
      className={
        filterClasses("group relative flex w-full h-[58px] items-center gap-4 px-3 rounded-lg group transition-colors duration-200 ease-in-out hover:bg-grey-200",
          conversation?.user?.id === selectedUserId ? "bg-grey-100" : "bg-white",
          className
        )}
      {...rest}
    >
      {/* avatar */}
      <div className="flex items-center flex-1 min-w-0 space-x-3">
        <div className={filterClasses(
          "flex shrink-0 items-center justify-center rounded-full size-[32px] space-x-3 relative",
          `${ChannelColors[conversation?.lastUserMessageChannel || Channel.WEB].bg} text-white`
        )}>
          <span className="font-semibold text-lg">{firstLetterOfName}</span>

          {/* online indicator */}
          <div className={
            filterClasses(
              "size-2.5 rounded-full transition-all duration-200 absolute bottom-0 -right-1.5 outline outline-[0.15rem] group-hover:outline-grey-200",
              isSelected ? "outline-grey-100" : "outline-white",
              isOnline || UsersOnlineManager.isConversationFresh(conversation?.user?.id) ? "bg-teal-450 border border-white" : "bg-white border border-grey-700"
            )}
          />
        </div>

        {/* customer and last message */}
        <div className="flex flex-col overflow-hidden min-w-0">
          <h3 className="text-sm font-semibold truncate">{conversation?.user?.name || conversation?.name}</h3>
          <p className="text-sm text-grey-600 truncate">{conversation?.lastUserMessage}</p>
        </div>
      </div>

      {/* time of last message, last channel, and takeover agent */}
      <div className="relative flex flex-col min-h-[40px] shrink-0 items-end transition-opacity ease-in-out">
        {!isEscalated &&
          <div
            aria-hidden={true}
            className="absolute inset-0 z-50 flex items-center justify-end opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 transition-opacity duration-200 ease-in-out pointer-events-none group-hover:pointer-events-auto group-focus-within:pointer-events-auto"
          >
            <Button
              loading={isPending}
              variant={ButtonVariant.GHOST}
              onClick={(e) => {
                e.preventDefault();
                handleArchiveUser(conversation?.user?.id)
              }}
              onFocus={(e) => e.currentTarget.setAttribute("aria-hidden", "false")}
            >
              Close
            </Button>
          </div>
        }

        <div className={filterClasses(
          "text-grey-600 text-sm inset-0 opacity-100 flex flex-col items-end text-right justify-end transition-opacity duration-200 ease-in-out",
          !isEscalated && "group-hover:opacity-0 group-focus-within:opacity-0"
        )}>
          {conversation?.lastUserMessageAt && conversation?.lastUserMessageChannel ? <p>{parseTime(conversation.lastUserMessageAt)} | {conversation.lastUserMessageChannel}</p> : null}
          {/* todo: new message indicator - add this back in after getting clarity from design */}
          {/* {totalNewMessageCount > 0 ? (
            <div className="flex items-center justify-center size-[14px] text-white bg-red-550 rounded-full mb-1">
              <span className="text-[10.5px] font-semibold leading-[10.5px]">{totalNewMessageCount}</span>
            </div>
          ) : null} */}
          {
            currentEscalation?.assignedMemberId ? (
              <>
                {/* takeover agent indicator */}
                <div className="flex items-center space-x-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="10">
                    <title>takeover icon</title>
                    <path
                      className="fill-grey-600"
                      d="M9.6000004 0L0.40000001 0C0.17874999 0 0 0.17874999 0 0.40000001L0 9.6000004C0 9.82125 0.17874999 10 0.40000001 10L9.6000004 10C9.82125 10 10 9.82125 10 9.6000004L10 0.40000001C10 0.17874999 9.82125 0 9.6000004 0ZM6.8337498 5.0812502L3.75875 7.3062501C3.6925001 7.3537502 3.5999999 7.3062501 3.5999999 7.2249999L3.5999999 6.6387501C3.5999999 6.51125 3.6612501 6.3899999 3.7650001 6.3150001L5.5825 5L3.7650001 3.6849999C3.6612501 3.6099999 3.5999999 3.49 3.5999999 3.3612499L3.5999999 2.7750001C3.5999999 2.6937499 3.6925001 2.64625 3.75875 2.6937499L6.8337498 4.9187498C6.8887501 4.9587498 6.8887501 5.0412502 6.8337498 5.0812502Z"
                      fillRule="evenodd"
                    />
                  </svg>

                  <p>{agentName}</p>
                </div>
              </>
            ) : null}
        </div>
      </div>
    </div>
  )
}

